import React from 'react';

// Horizontal value-chain banner: Farm → (in transit) → Feedlot → (in transit)
// → Abattoir, with a slaughtered end-cap. Mirrors the admin dashboard's
// "Live animal tracker" banner (same container padding, 7xl stage numbers, dashed
// connector, and transit truck glyphs) so the deal overview reads identically,
// but is scoped to a single deal's tracking data.

const fmtNum = (value: number) => new Intl.NumberFormat().format(Math.max(0, Math.round(value || 0)));

const TruckIcon = () => (
  <svg
    className="h-8 w-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 7h11v10H3z" />
    <path d="M14 10h3.4L21 13.8V17h-7z" />
    <path d="M5.5 17a2 2 0 1 0 4 0" />
    <path d="M16.5 17a2 2 0 1 0 4 0" />
    <path d="M3 17h2.5" />
    <path d="M9.5 17h7" />
  </svg>
);

function TransitLeg({ count, label }: { count: number; label: string }) {
  return (
    <div className="relative flex justify-center">
      <div className="inline-flex min-w-24 flex-col items-center gap-0.5 bg-white px-5">
        <div className="text-4xl font-semibold leading-none tracking-tight text-avcs-muted">{fmtNum(count)}</div>
        <div className="text-avcs-muted/80" aria-label={label} title={label}>
          <TruckIcon />
        </div>
      </div>
    </div>
  );
}

function Stage({
  count,
  label,
  align = 'left',
}: {
  count: number;
  label: string;
  align?: 'left' | 'center' | 'right';
}) {
  const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const numPad = align === 'center' ? 'px-5' : align === 'right' ? 'pl-5' : 'pr-5';
  return (
    <div className={`relative ${textAlign}`}>
      <div className={`inline-block bg-white ${numPad} text-7xl font-bold leading-none tracking-tight text-avcs-text`}>
        {fmtNum(count)}
      </div>
      <div className="mt-4 text-sm font-semibold uppercase tracking-wide text-avcs-muted">{label}</div>
    </div>
  );
}

export interface ValueChainBannerProps {
  farm: number;
  farmToFeedlot: number;
  feedlot: number;
  feedlotToAbattoir: number;
  abattoir: number;
  slaughtered?: number;
  mortalities?: number;
  title?: string;
  className?: string;
  /** localStorage key for the collapsed preference. */
  storageKey?: string;
}

export const ValueChainBanner = React.memo(function ValueChainBanner({
  farm,
  farmToFeedlot,
  feedlot,
  feedlotToAbattoir,
  abattoir,
  slaughtered = 0,
  mortalities = 0,
  title = 'Live animal tracker',
  className = '',
  storageKey = 'avcs.liveAnimalTracker.visible',
}: ValueChainBannerProps) {
  // Collapsed state is remembered, so someone who works mostly in the KPI cards
  // isn't re-hiding it on every deal they open. Defaults to visible.
  const [visible, setVisible] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(storageKey) !== 'hidden';
    } catch {
      return true;  // private mode / storage disabled
    }
  });

  const toggle = () => {
    setVisible((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(storageKey, next ? 'visible' : 'hidden');
      } catch {
        /* preference just won't persist */
      }
      return next;
    });
  };

  return (
    <div className={`border border-[#1e6bb824] bg-white ${visible ? 'p-8' : 'px-8 py-4'} ${className}`}>
      <div className={`flex flex-wrap items-center justify-between gap-2 ${visible ? 'mb-6' : ''}`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-[#1e6bb833] bg-white px-2 py-0.5 text-xs font-semibold text-[#1e6bb8] hover:bg-[#f8fbff]"
            onClick={toggle}
            aria-expanded={visible}
            aria-controls="live-animal-tracker-stages"
          >
            {visible ? 'Hide' : 'Show'}
          </button>
          <div className="text-sm font-semibold uppercase tracking-wide text-avcs-muted">{title}</div>
        </div>
        {(slaughtered > 0 || mortalities > 0) && (
          <div className="flex items-center gap-4 text-xs font-semibold uppercase tracking-wide">
            {slaughtered > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[#7b4a18]">
                <span className="h-2 w-2 rounded-full bg-[#c8791f]" aria-hidden />
                {fmtNum(slaughtered)} slaughtered
              </span>
            )}
            {mortalities > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[#6b1010]">
                <span className="h-2 w-2 rounded-full bg-[#c0392b]" aria-hidden />
                {fmtNum(mortalities)} mortalities
              </span>
            )}
          </div>
        )}
      </div>
      <div className="relative" id="live-animal-tracker-stages" hidden={!visible}>
        <div
          className="pointer-events-none absolute left-0 right-[12%] top-10 hidden border-t border-dashed border-[#1e6bb84d] md:block"
          aria-hidden="true"
        />
        <div className="relative z-10 grid gap-4 md:grid-cols-5">
          <Stage count={farm} label="Animals at farm" align="left" />
          <TransitLeg count={farmToFeedlot} label="In transit to feedlot" />
          <Stage count={feedlot} label="Animals at feedlot" align="center" />
          <TransitLeg count={feedlotToAbattoir} label="In transit to abattoir" />
          <Stage count={abattoir} label="Animals at abattoir" align="right" />
        </div>
      </div>
    </div>
  );
});

export default ValueChainBanner;
